import {
  Avatar,
  Box,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CreditCard,
  MoreVertical,
  Search,
  UserCheck,
  Users as UsersIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import PageHeader from "../../../common/components/PageHeader";
import { toast } from "../../../common/components/Toast";
import { SPACING } from "../../../common/constants/spacing";
import { loginAsUser, useUsersList, useUsersStats } from "../../api/admin";
import { StatCard } from "../../shared/components/StatCard";

function Users() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const pageSize = 20;

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when search term changes
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { users, totalCount, totalPages, error } = useUsersList(
    currentPage,
    pageSize,
    debouncedSearchTerm,
  );

  const { usersStats } = useUsersStats();

  useEffect(() => {
    if (error) {
      toast.error("Failed to load users");
      console.error("Error fetching users:", error);
    }
  }, [error]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleMenuOpen = (event, userId) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  const handleLoginAsUser = async () => {
    if (!selectedUserId) return;

    setLoggingIn(true);
    handleMenuClose();

    try {
      // Save admin token before switching
      const adminToken = localStorage.getItem("accessToken");
      localStorage.setItem("adminToken", adminToken);

      const response = await loginAsUser(selectedUserId);

      // Store the new tokens
      localStorage.setItem("accessToken", response.access);

      // Show success message
      toast.success(response.message || "Successfully logged in as user");

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        window.location.href = "/";
      }, 1000);
    } catch (error) {
      console.error("Error logging in as user:", error);
      localStorage.removeItem("adminToken");
      toast.error(
        error.response?.data?.error ||
          "Failed to login as user. Please try again.",
      );
      setLoggingIn(false);
    }
  };

  const handleMenuAction = (action) => {
    console.log(`Action ${action} for user ${selectedUserId}`);
    toast.info(`Action: ${action}`);
    handleMenuClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "error";
      case "user":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? "success" : "default";
  };

  return (
    <>
      <PageHeader
        title="Users"
        description="Manage all users, view their details, and perform administrative actions."
      />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Total Users"
            value={totalCount}
            icon={UsersIcon}
            color="#1976d2"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Active Users"
            value={users.filter((user) => user.is_active).length}
            icon={UserCheck}
            color="#2e7d32"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Subscribed Users"
            value={usersStats?.paid_users || 0}
            icon={CreditCard}
            color="#ed6c02"
          />
        </Grid>
      </Grid>

      <TextField
        fullWidth
        placeholder="Search users by name, email, or username..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={20} />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 400, mb: SPACING.sm }}
      />

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Email Verified</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length > 0 ? (
              users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={user.avatar}
                        sx={{ width: 40, height: 40 }}
                      />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 500 }}
                        >
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        user.role.charAt(0).toUpperCase() + user.role.slice(1)
                      }
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? "Active" : "Inactive"}
                      color={getStatusColor(user.is_active)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_email_verified ? "Yes" : "No"}
                      color={user.is_email_verified ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">
                      {formatDate(user.date_joined)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">
                      {formatDate(user.last_login)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, user.id)}
                      disabled={loggingIn}
                    >
                      <MoreVertical size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    {debouncedSearchTerm
                      ? "No users found matching your search"
                      : "No users found"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleLoginAsUser}>Login as User</MenuItem>
        <MenuItem onClick={() => handleMenuAction("View Details")}>
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction("Edit User")}>
          Edit User
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction("Change Role")}>
          Change Role
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuAction("Deactivate")}
          sx={{ color: "error.main" }}
        >
          Deactivate User
        </MenuItem>
      </Menu>
    </>
  );
}

export default Users;
